(() => {
  function initialize(dialog) {
    if (dialog.dataset.initialized || !dialog.showModal) return;
    dialog.dataset.initialized = 'true';
    const storageKey = 'mediamall-exit-offer';
    const pendingKey = 'mediamall-exit-offer-pending';
    const designMode = dialog.dataset.designMode === 'true';
    const started = Date.now();
    let shown = false;
    let previousFocus;
    let previousOverflow;
    const read = (key) => { try { return localStorage.getItem(key); } catch (_) { return null; } };
    const save = (key, value) => { try { localStorage.setItem(key, value); } catch (_) {} };
    const session = (value) => { try { if (value === undefined) return sessionStorage.getItem(pendingKey); if (value === null) sessionStorage.removeItem(pendingKey); else sessionStorage.setItem(pendingKey, value); } catch (_) {} };
    function hasOpenDialog() {
      return [...document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"], modal-dialog[open], popup-overlay[open]')].some(element => {
        if (element === dialog) return false;
        // The theme keeps the cart and chat dialog markup in the DOM while closed.
        if (element.classList.contains('dropdown-modal')) return element.getAttribute('open') === 'true';
        if (element.classList.contains('mm-chat-panel')) return element.classList.contains('is-open');
        if (!element.getClientRects().length) return false;
        for (let ancestor = element; ancestor; ancestor = ancestor.parentElement) {
          const style = getComputedStyle(ancestor);
          if (ancestor.hidden || ancestor.getAttribute('aria-hidden') === 'true' || style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity) === 0) return false;
        }
        return true;
      });
    }
    function open(force = false) {
      if (dialog.open || (!force && (shown || designMode))) return;
      // Let phone shoppers browse the homepage without an automatic signup overlay.
      if (!force && document.body.classList.contains('template-index') && matchMedia('(max-width: 749px)').matches) return;
      if (!force && (Date.now() - started < Number(dialog.dataset.delay) * 1000 || Date.now() - Number(read(storageKey)) < Number(dialog.dataset.days) * 86400000)) return;
      if (hasOpenDialog()) return;
      previousFocus = document.activeElement;
      previousOverflow = document.body.style.overflow;
      dialog.showModal();
      document.body.style.overflow = 'hidden';
      shown = true;
      if (!designMode) save(storageKey, String(Date.now()));
      (dialog.querySelector('input[type="email"]') || dialog.querySelector('[data-close]')).focus();
    }
    dialog.addEventListener('click', event => { if (event.target.closest('[data-close]')) dialog.close(); });
    dialog.addEventListener('click', event => {
      const rect = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) dialog.close();
    });
    dialog.addEventListener('close', () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    });
    document.addEventListener('mouseout', event => {
      if (!event.relatedTarget && event.clientY <= 0 && matchMedia('(hover: hover) and (pointer: fine)').matches) open();
    });
    // Mobile browsers expose no reliable exit signal. Use a deliberate upward-scroll fallback.
    let lastScroll = window.scrollY;
    let upwardDistance = 0;
    window.addEventListener('scroll', () => {
      const current = window.scrollY;
      if (matchMedia('(hover: none)').matches) {
        upwardDistance = current < lastScroll ? upwardDistance + lastScroll - current : 0;
        if (upwardDistance >= 180 && lastScroll > 300) open();
      }
      lastScroll = current;
    }, { passive: true });
    const form = dialog.querySelector('form');
    let submitting = false;
    let button;
    let originalLabel;
    let verificationTimeout;
    function showLoading() {
      button = form.querySelector('[type="submit"]');
      if (!button || button.disabled) return;
      originalLabel = button.innerHTML;
      button.disabled = true;
      button.innerHTML = '<span class="exit-offer__spinner" aria-hidden="true"></span> Signing you up…';
      form.setAttribute('aria-busy', 'true');
      const status = form.querySelector('[data-submit-status]');
      status.hidden = false;
      status.textContent = 'Please wait while we securely confirm your signup.';
      verificationTimeout = setTimeout(() => {
        if (submitting || !button?.isConnected) return;
        form.removeAttribute('aria-busy');
        button.disabled = false;
        button.innerHTML = originalLabel;
        status.textContent = 'Verification is taking longer than expected. Please try again.';
      }, 30000);
    }
    async function submitInPlace() {
      if (submitting || !form.querySelector('[type="submit"]')) return;
      submitting = true;
      session(dialog.id);
      showLoading();
      clearTimeout(verificationTimeout);
      const status = form.querySelector('[data-submit-status]');
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 30000);
      try {
        const body = new URLSearchParams(new FormData(form));
        const response = await fetch(form.action, { method: 'POST', body, credentials: 'same-origin', signal: controller.signal });
        const result = new DOMParser().parseFromString(await response.text(), 'text/html');
        const returnedDialog = result.getElementById(dialog.id);
        const success = returnedDialog?.querySelector('[data-success]');
        const error = returnedDialog?.querySelector('[data-error]');
        if (response.ok && success) {
          form.innerHTML = returnedDialog.querySelector('form').innerHTML;
          session(null);
          form.querySelector('h2')?.focus();
        } else if (error) {
          status.textContent = error.textContent.trim();
        } else if (new URL(response.url).pathname.includes('/challenge') || result.querySelector('#shopify-challenge, .shopify-challenge__container')) {
          status.textContent = 'Shopify needs an additional verification step. You’ll return to your coupon afterward.';
          // Interactive challenges must use Shopify's own verification page.
          HTMLFormElement.prototype.submit.call(form);
        } else {
          status.textContent = 'We couldn’t confirm your signup. Please try again.';
        }
      } catch (_) {
        status.textContent = 'We couldn’t connect. Please check your connection and try again.';
      } finally {
        clearTimeout(timeout);
        submitting = false;
        form.removeAttribute('aria-busy');
        if (button?.isConnected) { button.disabled = false; button.innerHTML = originalLabel; }
      }
    }
    if (form && form.querySelector('[type="submit"]')) {
      // Shopify wraps form.submit to obtain a verification token before calling it.
      // Supply the in-place submission first, so that verified callback stays here.
      form.submit = submitInPlace;
      window.Shopify?.captcha?.protect?.(form);
      document.addEventListener('submit', event => {
        if (event.target === form) { session(dialog.id); showLoading(); }
      }, true);
      document.addEventListener('submit', event => {
        if (event.target !== form || event.defaultPrevented) return;
        event.preventDefault();
        submitInPlace();
      });
    }
    dialog.addEventListener('click', async event => {
      const copy = event.target.closest('[data-copy]');
      if (!copy) return;
      const status = dialog.querySelector('[data-copy-status]');
      try { await navigator.clipboard.writeText(copy.dataset.code); status.textContent = 'Copied! Your code is ready for checkout.'; }
      catch (_) { status.textContent = 'Select the code above to copy it, or use the button below to apply it.'; }
    });
    if (dialog.querySelector('[data-success]') || (session() === dialog.id && dialog.querySelector('[data-error]'))) {
      session(null);
      open(true);
    }
    if (designMode) open(true);
    document.addEventListener('shopify:section:select', event => {
      if (designMode && event.detail.sectionId === dialog.dataset.sectionId) open(true);
    });
  }
  document.querySelectorAll('[data-exit-offer]').forEach(initialize);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-exit-offer]').forEach(initialize));
})();
